import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { Button, Input, Tabs, Tooltip, Select } from 'antd';
import {
  PlusCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RoleChecker, { useCheckRole } from '@components/RoleChecker';
import roles from '@utils/rolesConstants';
import Table from '@components/Table';
import useSwr from '@hooks/useSwr';
import {
  bytesFormat,
  extractDateInISOFormat,
  formatCurrency,
  formatPricePerUnit,
} from '@utils/formatters';
import { IObject } from '@utils/defaultTypes';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import SessionContext from '@providers/Session/SessionContext';
import SelectCustomers from '@components/SelectCustomers';
import useSearchParams from '@hooks/useSearchParams';
import CustomTag from '@components/CustomTag';
import AdminActionsRequest from './AdminActionsRequest';
import NewRequestModal from './NewRequestModal';

import * as S from './styles';

const { Search } = Input;
const { TabPane } = Tabs;

interface IRequests {
  isInTheContractDetails?: boolean;
  defaultCustomerId?: number;
  defaultContractId?: number;
  defaultFilters?: IObject;
  defaultColumns?: string[];
}

const Requests: React.FC<IRequests> = ({
  isInTheContractDetails,
  defaultCustomerId,
  defaultContractId,
  defaultFilters,
  defaultColumns,
}) => {
  const { keycloak } = useContext(KeycloakContext);
  const { hasCustomersRoles, selectedApp, isNotChildOfRoot } =
    useContext(SessionContext);
  const customerType = keycloak?.idTokenParsed?.customerType;
  const isEndCustomer = customerType === 'CUSTOMER';
  const canManage = useCheckRole({ role: roles.MANAGE_REQUESTS_MOT });

  const navigate = useNavigate();

  const { t } = useTranslation(['requests', 'attributes', 'contracts']);

  const [modalVisible, setModalVisible] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState('');

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['showContracted'],
    extraParams: ['description', 'customerId', 'contractId'],
  });

  const {
    showContracted,
    description,
    customerId,
    contractId,
    page,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  const treatedShowContracted = useMemo(() => {
    if (isEndCustomer) {
      return 'true';
    }
    return showContracted !== 'archived' ? 'false' : undefined;
  }, [isEndCustomer, showContracted]);

  const { data, mutate } = useSwr(
    `/service-proxy/broker/virtual-account${
      showContracted === 'archived' ? '/archived' : ''
    }`,
    {
      params: {
        page: page ? Number(page) - 1 : 0,
        linesPerPage: linesPerPage ?? 20,
        showContracted: treatedShowContracted,
        name: description,
        customerId,
        contractId,
        ...defaultFilters,
      },
    },
  );

  const { data: contractsByClient } = useSwr(
    customerId ? `/service-proxy/broker/${customerId}/virtual-plan` : null,
  );

  const contractsByClientOptions = useMemo(
    () =>
      contractsByClient?.map(
        ({ id, planDesc }: { id: boolean; planDesc: string }) => ({
          label: planDesc,
          value: id,
        }),
      ),
    [contractsByClient],
  );

  useEffect(() => {
    setDescriptionValue(description);
  }, [description]);

  const adminActions = useCallback(
    (_: any, record: any) => (
      <AdminActionsRequest
        data={data}
        mutate={mutate}
        showArchived={showContracted !== 'archived' && !isInTheContractDetails}
        item={record}
      />
    ),
    [data, mutate, showContracted, isInTheContractDetails],
  );

  const contractDescriptionLink = useCallback(
    (_: any, record: any) =>
      record.virtualContract?.id ? (
        <Tooltip title={t('view-contract')}>
          <Button
            type="link"
            onClick={() => {
              navigate(
                `/${selectedApp}/mobile-network/financial/contracts/${
                  record.virtualContract?.id
                }${showContracted ? `?showContracted=${showContracted}` : ''}`,
              );
            }}
          >
            {record.virtualContract?.planDesc}
          </Button>
        </Tooltip>
      ) : (
        record.virtualContract?.planDesc
      ),
    [t, navigate, showContracted, selectedApp],
  );

  const sharingType = useCallback(
    (_: any, record: { virtualContract: any }) => {
      const { virtualContract } = record;
      if (virtualContract?.payPerUseFlag === 'S') {
        return t('attributes:type.payPerUse');
      }

      return t(`attributes:type.${virtualContract?.sharingType}`);
    },
    [t],
  );

  const activatedTag = useCallback(
    (value: boolean) => (
      <CustomTag color={value ? 'green' : 'red'}>
        {t(
          `attributes:automaticBlocking.${value ? 'activated' : 'disabled'}`,
        ).toUpperCase()}
      </CustomTag>
    ),
    [t],
  );

  const columns = useMemo(() => {
    const allColumns = [
      { title: t('columns.id'), dataIndex: 'id' },
      {
        title: t('columns.description'),
        dataIndex: 'name',
        render: (value: number, record: any) => {
          const isError = record.status === 'ERROR';

          return isError ? (
            <>
              <Tooltip title={t('requests:error-creating-order')}>
                <Button
                  danger
                  type="link"
                  icon={<ExclamationCircleOutlined />}
                  style={{
                    position: 'absolute',
                    left: 4,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                />
              </Tooltip>
              <span>{value}</span>
            </>
          ) : (
            value
          );
        },
      },
      {
        title: t('columns.customerName'),
        dataIndex: 'customerName',
        render: (_: any, record: any) => record.customer?.name,
        visible: showContracted !== 'true',
      },
      {
        title: t('columns.planDesc'),
        dataIndex: 'planDesc',
        align: 'center',
        render: contractDescriptionLink,
      },
      {
        title: t('attributes:type.title'),
        dataIndex: 'sharingType',
        align: 'center',
        render: sharingType,
      },
      {
        title: t('attributes:franchise'),
        dataIndex: 'franchiseBytes',
        align: 'center',
        render: (value: any, record: any) => {
          const franchise = value ?? record?.virtualContract?.franchise;
          return bytesFormat(franchise);
        },
      },
      {
        title: t('attributes:monthlyPayment'),
        dataIndex: 'value',
        align: 'center',
        render: (value: string) => formatCurrency(Number(value ?? 0)),
      },
      {
        title: t('attributes:activationPrice'),
        dataIndex: 'activationPrice',
        align: 'center',
        render: (value: string) => formatCurrency(Number(value ?? 0)),
      },
      {
        title: t('attributes:excMbValue'),
        dataIndex: 'megabyteExcPrice',
        align: 'center',
        render: formatPricePerUnit,
      },
      {
        title: t('attributes:activationDate'),
        dataIndex: 'activationDate',
        align: 'center',
        render: (value: string) => value && extractDateInISOFormat(value),
      },
      {
        title: t('attributes:nbPreActivationDays'),
        dataIndex: 'nbPreActivationDays',
        align: 'center',
        render: (value: number) =>
          value > 0
            ? t(`attributes:day${value > 1 ? '_plural' : ''}`, {
                count: value,
              })
            : t('attributes:noPreActivation'),
      },
      {
        title: t('attributes:automaticBlocking.title'),
        dataIndex: 'automaticBlocking',
        align: 'center',
        render: activatedTag,
      },
      {
        title: t('columns.actions'),
        dataIndex: 'actions',
        align: 'center',
        render: adminActions,
      },
    ].filter(col => col.visible !== false);

    if (defaultColumns) {
      return allColumns?.filter(column =>
        defaultColumns.includes(column.dataIndex),
      );
    }

    return allColumns;
  }, [
    t,
    showContracted,
    contractDescriptionLink,
    sharingType,
    activatedTag,
    adminActions,
    defaultColumns,
  ]);

  const handleNewRequestMutate = useCallback(
    (newRequest: any) => {
      mutate({
        ...data,
        content: [newRequest, ...data.content].slice(0, 10),
      });
    },
    [data, mutate],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible:
          hasCustomersRoles &&
          !isInTheContractDetails &&
          showContracted !== 'true',
        label: t('columns.customerName'),
        element: (
          <SelectCustomers
            value={customerId && Number(customerId)}
            onChange={value => setSearchParams({ customerId: value })}
          />
        ),
      },
      {
        visible: contractsByClientOptions,
        label: t('columns.planDesc'),
        element: (
          <Select
            optionFilterProp="label"
            options={contractsByClientOptions}
            loading={!contractsByClientOptions}
            disabled={!contractsByClientOptions}
            style={{ width: '100%' }}
            allowClear
            showSearch
            value={contractId && Number(contractId)}
            onChange={value => setSearchParams({ contractId: value })}
          />
        ),
      },
      {
        visible: true,
        label: t('columns.description'),
        element: (
          <Search
            onSearch={value => setSearchParams({ description: value })}
            onChange={({ target: { value } }) => setDescriptionValue(value)}
            value={descriptionValue}
            allowClear
          />
        ),
      },
    ],
    [
      t,
      setSearchParams,
      hasCustomersRoles,
      customerId,
      descriptionValue,
      contractId,
      contractsByClientOptions,
      isInTheContractDetails,
      showContracted,
    ],
  );

  const handleTabsChange = useCallback(
    (key: any) => {
      setSearchParams({ showContracted: key });
    },
    [setSearchParams],
  );

  const isErrorStatus = useCallback((status: string) => status === 'ERROR', []);

  const rowClassName = useCallback(
    (record: any) =>
      isErrorStatus(record?.status) && 'request-creation-error-table-cell',
    [isErrorStatus],
  );

  return (
    <S.Container>
      {modalVisible && (
        <RoleChecker role={roles.MANAGE_REQUESTS_MOT}>
          <NewRequestModal
            visible={modalVisible}
            newRequestMutate={handleNewRequestMutate}
            onClose={() => setModalVisible(false)}
            defaultContractId={defaultContractId}
            defaultCustomerId={defaultCustomerId}
            showContracted={showContracted}
          />
        </RoleChecker>
      )}
      {customerType !== 'CUSTOMER' && !isInTheContractDetails && (
        <Tabs
          type="card"
          tabBarStyle={{ margin: 0 }}
          defaultActiveKey="receive"
          activeKey={showContracted}
          onChange={handleTabsChange}
          style={{ background: '#ffffff' }}
        >
          <TabPane key="receive" tab={t('visions-steps.allocated')} />
          {customerType === 'BROKER' && isNotChildOfRoot && (
            <TabPane key="true" tab={t('visions-steps.pay')} />
          )}
          <TabPane key="archived" tab={t('visions-steps.archived')} />
        </Tabs>
      )}
      <Table
        title={t('title')}
        // @ts-ignore
        columns={columns}
        data={data?.content}
        rowClassName={rowClassName}
        loading={!data?.content}
        tableKey={isInTheContractDetails ? 'contractDetails' : 'requests'}
        extraActions={
          !isEndCustomer &&
          canManage && (
            <Tooltip title={t('newRequest')}>
              <Button
                icon={<PlusCircleOutlined />}
                onClick={() => setModalVisible(true)}
              />
            </Tooltip>
          )
        }
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        onTableChange={setSearchParams}
        pagination={{ page, linesPerPage, total: data?.totalElements }}
      />
    </S.Container>
  );
};

export default Requests;
